import { format, formatDistanceToNow, fromUnixTime, isValid, parseISO } from 'date-fns';

export const DATE_TIME_FORMAT = 'MMM d, yyyy • HH:mm';

const parseDateValue = (value: string | number | Date | null | undefined): Date | null => {
    if (value == null) {
        return null;
    }

    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return null;
        }
        const candidate = Math.abs(value) >= 1e12 ? new Date(value) : fromUnixTime(value);
        return isValid(candidate) ? candidate : null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const parsedIso = parseISO(trimmed);
        if (isValid(parsedIso)) {
            return parsedIso;
        }

        const numericValue = Number(trimmed);
        if (!Number.isNaN(numericValue)) {
            const numericCandidate = Math.abs(numericValue) >= 1e12
                ? new Date(numericValue)
                : fromUnixTime(numericValue);
            if (isValid(numericCandidate)) {
                return numericCandidate;
            }
        }

        const parsedGeneric = new Date(trimmed);
        return isValid(parsedGeneric) ? parsedGeneric : null;
    }

    return null;
};

export const resolveDate = (value: string | number | Date | null | undefined): Date | null =>
    parseDateValue(value);

export const formatRelativeTime = (value: string | number | Date): string => {
    const date = parseDateValue(value);
    if (!date) {
        return typeof value === 'string' ? value : '';
    }
    return formatDistanceToNow(date, { addSuffix: true });
};

export const formatDateTime = (
    value: string | number | Date | null | undefined,
    pattern: string = DATE_TIME_FORMAT,
    fallback: string = 'Unknown',
): string => {
    const date = parseDateValue(value);
    if (!date) {
        return fallback;
    }
    return format(date, pattern);
};

export default formatRelativeTime;
